// src/UIDesigning_In_Noida.tsx

import React, { useState } from "react"; // Import useState
import { motion } from "framer-motion";

import { FaLaptopCode, FaCertificate, FaBriefcase, FaFileAlt, FaComments, FaUsers } from 'react-icons/fa';
import { Helmet } from "react-helmet-async";

// Placeholder import for UI/UX main image
import Img2 from "../../assets/Internship/UI.png"; // Using a generic image, ideally a UI/UX specific one

import JoinNowFormPopup from "../../componants/JoinNowFormPopup";


// Placeholder imports for logos - these assume images are in your public folder
// If you prefer to import them directly from an assets folder, adjust paths accordingly.

// UI/UX technology logos (placeholders - replace with actual paths if available)
import figma from "../../assets/Internship/figma.jpg"; // Example: path to Figma logo
import sketch from "../../assets/Internship/sketch.png"; // Example: path to Sketch logo
import xd from "../../assets/Internship/Adobe_XD.png";     // Example: path to Adobe XD logo
import photoshop from "../../assets/Internship/Photoshop.webp"; // Example: path to Photoshop logo
import illustrator from "../../assets/Internship/Illustrator.jpg"; // Example: path to Illustrator logo
import invision from "../../assets/Internship/invision.png"; // Example: path to InVision logo


import img from "../../assets/Internship/our-process.webp";

// Animation variants
const fadeInLeft = {
  hidden: { opacity: 0, x: -80 },
  visible: { opacity: 1, x: 0, transition: { duration: 0.8 } },
};

const fadeInRight = {
  hidden: { opacity: 0, x: 80 },
  visible: { opacity: 1, x: 0, transition: { duration: 0.8 } },
};



const UIDesigningInNoida: React.FC = () => {
  const [isPopupOpen, setIsPopupOpen] = useState(false); // State to control popup visibility
  const [selectedCourseTitle, setSelectedCourseTitle] = useState(""); // State to store the course title

  const handleOpenPopup = (courseTitle: string) => { // Function to open the popup
    setSelectedCourseTitle(courseTitle); // Set the selected course title
    setIsPopupOpen(true); // Open the popup
  };

  const handleClosePopup = () => { // Function to close the popup
    setIsPopupOpen(false); // Close the popup
    setSelectedCourseTitle(""); // Clear selected course when closing
  };


  const whyChooseUsBenefits = [
    {
      icon: <FaLaptopCode className="text-blue-400 text-5xl mb-4" />,
      title: "Hybrid Mode Class",
      description: "Learn flexibly with combined online and offline classroom training sessions.",
    },
    {
      icon: <FaCertificate className="text-blue-400 text-5xl mb-4" />,
      title: "Internship Certificate",
      description: "Receive an internship certificate for hands-on industry project experience.",
    },
    {
      icon: <FaBriefcase className="text-blue-400 text-5xl mb-4" />,
      title: "100% Placement Assistance",
      description: "Complete placement support provided until you secure a suitable job.",
    },
    {
      icon: <FaFileAlt className="text-blue-400 text-5xl mb-4" />,
      title: "CV Building & ATS Score",
      description: "Optimize your resume for ATS and improve shortlisting chances significantly.",
    },
    {
      icon: <FaComments className="text-blue-400 text-5xl mb-4" />,
      title: "Mock Interviews",
      description: "Practice with mock interviews to boost real interview performance confidence.",
    },
    {
      icon: <FaUsers className="text-blue-400 text-5xl mb-4" />,
      title: "15 Students Per Batch",
      description: "Small batches ensure personalized attention and better student-teacher interaction.",
    },
  ];

  return (
    <div className="min-h-screen bg-gray-100 font-sans antialiased">
      <Helmet>
        <title>UI/UX Designing Training in Noida | Appwars Technologies</title>
        <meta name="description" content="Master User Interface (UI) and User Experience (UX) Design with comprehensive training in Noida. Learn design principles, wireframing, prototyping, and user testing. 100% placement, hybrid classes, and internship certificates by Appwars Technologies." />
        <meta name="keywords" content="UI design, UX design, user interface, user experience, design principles, wireframing, prototyping, user testing, Noida, Figma, Sketch, Adobe XD, Photoshop, Illustrator, InVision, training, placement, internship, Appwars Technologies, Nadezhda Ventures" />
        <meta name="author" content="Appwars Technologies / Nadezhda Ventures" />
        <meta property="og:title" content="UI/UX Designing Training in Noida | Appwars Technologies" />
        <meta property="og:description" content="Shape engaging digital experiences with professional UI/UX Design training programs in Noida." />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="http://yourwebsite.com/ui-ux-design-noida" /> {/* Replace with your actual website URL */}
        <link rel="canonical" href="http://yourwebsite.com/ui-ux-design-noida" /> {/* Replace with your actual canonical URL */}
      </Helmet>



      <main>
       {/* SECTION 1 - Hero Section with Transparent Image and Text Overlay */}
            <section className="relative overflow-hidden flex items-center justify-center h-[400px] md:h-[400px] lg:h-[400px]">
              {/* Transparent Background Image */}
              <div className="absolute inset-0 z-0">
                <img
                  src="https://png.pngtree.com/thumb_back/fh260/background/20190223/ourmid/pngtree-blue-fluttering-technology-background-boardbackground-image_83398.jpg" // Using a generic technology background image
                  alt="UI/UX Transparent Background"
                  className="w-full h-full object-cover opacity-80" // Adjust opacity for desired transparency
                />
                {/* Optional: Add a subtle overlay for better text readability and darker background */}
                <div className="absolute inset-0 bg-black opacity-70"></div> {/* Adjust opacity as needed */}
              </div>

              {/* Text and Image Content */}
              <div className="min-h-screen flex flex-col md:flex-row items-center justify-center gap-12 px-6 py-20 max-w-7xl mx-auto relative z-10">
                <motion.div
                  className="flex-1 text-center md:text-left"
                  initial="hidden"
                  whileInView="visible"
                  viewport={{ once: true }}
                  variants={fadeInLeft}
                >
                  <h1 className="text-4xl md:text-5xl font-extrabold text-orange-500 mb-6">
                    UI/UX Designing Training
                  </h1>
                  <p className="text-gray-300 text-lg leading-relaxed mb-6">
                    Craft intuitive and engaging digital experiences with Nadezhda Venture's comprehensive UI/UX Designing program. Learn user-centered design principles, tools, and methodologies.
                  </p>

                  <button
                    className="bg-orange-600 hover:bg-orange-700 px-6 py-3 rounded-lg font-semibold transition text-white"
                    onClick={() => handleOpenPopup("UI/UX Designing Training")} // Pass the course title
                  >
                    Join Now !
                  </button>
                </motion.div>

                <motion.div
                  className="flex-1 w-full max-w-2xl flex justify-center items-center"
                  initial="hidden"
                  whileInView="visible"
                  viewport={{ once: true }}
                  variants={fadeInRight}
                >
                  <img
                    src={Img2}
                    alt="UI/UX Showcase"
                    className="rounded-xl shadow-lg w-full h-auto object-contain max-h-[400px]"
                  />
                </motion.div>
              </div>
            </section>


       {/* UI/UX Technologies Covered Section */}
<section className="px-6 md:px-20 py-24 bg-gray-300 text-orange-600">
  <h2 className="text-5xl font-bold text-center mb-10">Key UI/UX Design Tools</h2>
  <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-6">
    {/* Figma */}
    <div className="flex flex-col items-center p-4 bg-white rounded-xl shadow-lg hover:scale-105 transition-transform">
      <img src={figma} alt="Figma Icon" className="w-16 h-16 mb-2"/>
      <h3 className="text-lg font-semibold text-center">Figma</h3>
      <button
        onClick={() => handleOpenPopup("Figma")} // Pass the course title
        className="mt-4 bg-orange-600 hover:bg-orange-700 text-white text-sm py-2 px-4 rounded-xl transition-all duration-300">Join Now</button>
    </div>

    {/* Sketch */}
    <div className="flex flex-col items-center p-4 bg-white rounded-xl shadow-lg hover:scale-105 transition-transform">
      <img src={sketch} alt="Sketch Icon" className="w-16 h-16 mb-2"/>
      <h3 className="text-lg font-semibold text-center">Sketch</h3>
      <button
        onClick={() => handleOpenPopup("Sketch")} // Pass the course title
        className="mt-4 bg-orange-600 hover:bg-orange-700 text-white text-sm py-2 px-4 rounded-xl transition-all duration-300">Join Now</button>
    </div>

    {/* Adobe XD */}
    <div className="flex flex-col items-center p-4 bg-white rounded-xl shadow-lg hover:scale-105 transition-transform">
      <img src={xd} alt="Adobe XD Icon" className="w-18 h-16 mb-2"/>
      <h3 className="text-lg font-semibold text-center">Adobe XD</h3>
      <button
        onClick={() => handleOpenPopup("Adobe XD")} // Pass the course title
        className="mt-4 bg-orange-600 hover:bg-orange-700 text-white text-sm py-2 px-4 rounded-xl transition-all duration-300">Join Now</button>
    </div>

    {/* Photoshop */}
    <div className="flex flex-col items-center p-4 bg-white rounded-xl shadow-lg hover:scale-105 transition-transform">
      <img src={photoshop} alt="Photoshop Icon" className="w-16 h-16 mb-2"/>
      <h3 className="text-lg font-semibold text-center">Photoshop</h3>
      <button
        onClick={() => handleOpenPopup("Photoshop")} // Pass the course title
        className="mt-4 bg-orange-600 hover:bg-orange-700 text-white text-sm py-2 px-4 rounded-xl transition-all duration-300">Join Now</button>
    </div>

    {/* Illustrator */}
    <div className="flex flex-col items-center p-4 bg-white rounded-xl shadow-lg hover:scale-105 transition-transform">
      <img src={illustrator} alt="Illustrator Icon" className="w-18 h-16 mb-2"/>
      <h3 className="text-lg font-semibold text-center">Illustrator</h3>
      <button
        onClick={() => handleOpenPopup("Illustrator")} // Pass the course title
        className="mt-4 bg-orange-600 hover:bg-orange-700 text-white text-sm py-2 px-4 rounded-xl transition-all duration-300">Join Now</button>
    </div>

    {/* InVision */}
    <div className="flex flex-col items-center p-4 bg-white rounded-xl shadow-lg hover:scale-105 transition-transform">
      <img src={invision} alt="InVision Icon" className="w-16 h-16 mb-2"/>
      <h3 className="text-lg font-semibold text-center">InVision</h3>
      <button
        onClick={() => handleOpenPopup("InVision")} // Pass the course title
        className="mt-4 bg-orange-600 hover:bg-orange-700 text-white text-sm py-2 px-4 rounded-xl transition-all duration-300">Join Now</button>
    </div>

   </div>

</section>

        {/* Why Choose Us Section (from image_ab7e7d.png, styling adapted) */}
        <section className="px-6 md:px-20 py-16 md:py-24 bg-white text-gray-800">
          <h2 className="text-3xl md:text-4xl font-bold text-center mb-10">Why Choose Us?</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 max-w-6xl mx-auto">
            {whyChooseUsBenefits.map((benefit, index) => (
              <div
                key={index}
                className="flex flex-col items-center text-center p-6 bg-gray-800 rounded-xl shadow-lg hover:shadow-xl transition-shadow duration-300"
              >
                {benefit.icon}
                <h3 className="text-xl font-semibold text-white mb-2">{benefit.title}</h3>
                <p className="text-gray-300 text-sm">{benefit.description}</p>
              </div>
            ))}
          </div>
        </section>


       {/* Our Interview Process Section */}
  <h2 className="text-3xl md:text-4xl font-bold text-center mb-10 text-orange-500">Our Interview Process</h2>

    <img
      src={img} // Path to your process image file
      alt="Our Interview Process"
      className="w-full h-auto object-cover"
    />

      </main>

     

      {/* Conditionally render the popup */}
      {isPopupOpen && (
        <JoinNowFormPopup
          onClose={handleClosePopup}
          courseTitle={selectedCourseTitle}
        />
      )}
    </div>
  );
};

export default UIDesigningInNoida;